import { useState, type FormEvent } from "react";
import type { Parte, TipoPersona } from "../../../shared/types";
import { Boton } from "../../components/Boton";
import { Campo, Input, Select } from "../../components/Formulario";
import { Modal } from "../../components/Modal";
import { Tabla, type ColumnaTabla } from "../../components/Tabla";
import { useToast } from "../../components/Toast";
import { api, ErrorApi } from "../../lib/apiClient";
import { validarDocumento } from "../../lib/validadores";

const TIPOS_PERSONA: TipoPersona[] = ["Persona Humana", "Persona Jurídica"];

export function PestanaPartes({
  expedienteId,
  partes,
  onCambio,
}: {
  expedienteId: string;
  partes: Parte[];
  onCambio: () => void;
}) {
  const { mostrar } = useToast();
  const [modalAbierto, setModalAbierto] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [rol, setRol] = useState("");
  const [nombreCompleto, setNombreCompleto] = useState("");
  const [tipoPersona, setTipoPersona] = useState<TipoPersona>("Persona Humana");
  const [documento, setDocumento] = useState("");
  const [telefono, setTelefono] = useState("");

  function limpiar() {
    setRol("");
    setNombreCompleto("");
    setTipoPersona("Persona Humana");
    setDocumento("");
    setTelefono("");
  }

  async function manejarEnvio(e: FormEvent) {
    e.preventDefault();
    const validacion = validarDocumento(tipoPersona, documento);
    if (!validacion.valido) {
      mostrar(validacion.mensaje, "error");
      return;
    }
    setGuardando(true);
    try {
      await api.post("/partes", {
        expediente_id: expedienteId,
        rol,
        nombre_completo: nombreCompleto,
        tipo_persona: tipoPersona,
        documento: documento || null,
        telefono: telefono || null,
      });
      mostrar("Parte agregada.", "exito");
      setModalAbierto(false);
      limpiar();
      onCambio();
    } catch (err) {
      mostrar(
        err instanceof ErrorApi ? err.message : "No se pudo agregar la parte.",
        "error",
      );
    } finally {
      setGuardando(false);
    }
  }

  const columnas: ColumnaTabla<Parte>[] = [
    { encabezado: "Rol", render: (p) => p.rol },
    { encabezado: "Nombre", render: (p) => p.nombre_completo },
    { encabezado: "Tipo", render: (p) => p.tipo_persona },
    { encabezado: "DNI / CUIT", render: (p) => p.documento ?? "—" },
    { encabezado: "Teléfono", render: (p) => p.telefono ?? "—" },
  ];

  return (
    <div>
      <div className="mb-3 flex justify-end">
        <Boton tamano="sm" onClick={() => setModalAbierto(true)}>
          + Nueva Parte
        </Boton>
      </div>
      <Tabla
        columnas={columnas}
        filas={partes}
        claveFila={(p) => p.id}
        tituloVacio="Sin partes registradas"
        descripcionVacio="Agregá la contraparte, testigos u otros involucrados."
      />
      <Modal
        titulo="Nueva parte"
        abierto={modalAbierto}
        onCerrar={() => setModalAbierto(false)}
      >
        <form onSubmit={manejarEnvio} className="space-y-4">
          <Campo etiqueta="Rol" obligatorio>
            <Input
              required
              value={rol}
              onChange={(e) => setRol(e.target.value)}
              placeholder="Demandado / Testigo"
            />
          </Campo>
          <Campo etiqueta="Nombre completo" obligatorio>
            <Input
              required
              value={nombreCompleto}
              onChange={(e) => setNombreCompleto(e.target.value)}
            />
          </Campo>
          <Campo etiqueta="Tipo de Persona" obligatorio>
            <Select
              value={tipoPersona}
              onChange={(e) => setTipoPersona(e.target.value as TipoPersona)}
            >
              {TIPOS_PERSONA.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </Select>
          </Campo>
          <div className="grid grid-cols-2 gap-4">
            <Campo etiqueta="DNI / CUIT">
              <Input
                value={documento}
                onChange={(e) => setDocumento(e.target.value)}
                placeholder={tipoPersona === "Persona Humana" ? "12345678" : "20-12345678-9"}
              />
            </Campo>
            <Campo etiqueta="Teléfono">
              <Input
                value={telefono}
                onChange={(e) => setTelefono(e.target.value)}
              />
            </Campo>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Boton
              type="button"
              variante="secundario"
              onClick={() => setModalAbierto(false)}
              disabled={guardando}
            >
              Cancelar
            </Boton>
            <Boton type="submit" cargando={guardando}>
              Guardar
            </Boton>
          </div>
        </form>
      </Modal>
    </div>
  );
}
